import { ECurrency } from 'App/i18n/i18n.types';

import {
  CarrierAndreani,
  CarrierCorreoArgentino,
  CarrierEPick,
  CarrierEnvia,
  NEEnviaDhlInternationalExpress,
  NEEnviaFedexInternationalGround
} from 'commons/utils/freight-type';

const DEFAULT_LOCALE = 'pt-BR';
const DEFAULT_CURRENCY = ECurrency.BRAZIL;
const ZERO_DECIMAL_PATTERN = /[.,]0+$/;

/**
 * Construir um `Intl.NumberFormat` é caro (resolucao de locale) e essas funcoes
 * sao chamadas por linha de lista. As combinacoes possiveis sao poucas
 * (3 locales x poucas moedas), entao cacheamos por locale + moeda + display.
 */
const formatterCache = new Map<string, Intl.NumberFormat>();

const getFormatter = (
  locale: string,
  currency: string,
  currencyDisplay?: 'symbol'
): Intl.NumberFormat => {
  const cacheKey = `${locale}|${currency}|${currencyDisplay ?? ''}`;
  const cached = formatterCache.get(cacheKey);

  if (cached) {
    return cached;
  }

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    ...(currencyDisplay ? { currencyDisplay } : {})
  });

  formatterCache.set(cacheKey, formatter);

  return formatter;
};

const format = (
  locale: string,
  currencyCode: string,
  amount: number,
  removeTrailingZeros = false
): any => {
  const normalizedLocale = locale || DEFAULT_LOCALE;
  const normalizedCurrency = currencyCode || DEFAULT_CURRENCY;

  const formattedValue = getFormatter(
    normalizedLocale,
    normalizedCurrency
  ).format(amount);

  if (!removeTrailingZeros) {
    return formattedValue;
  }

  // Remove trailing decimal zeros for whole numbers
  // Check if amount is integer or has zero decimal part
  if (Number.isInteger(amount) || amount % 1 === 0) {
    return formattedValue.replace(ZERO_DECIMAL_PATTERN, '');
  }
  return formattedValue;
};

const getCurrencySymbol = (locale: string, currency: ECurrency) => {
  const parts = getFormatter(locale, currency, 'symbol').formatToParts(0);

  const currencyPart = parts.find((part) => part.type === 'currency');

  return currencyPart?.value || '';
};

const argentinianCarriers = new Set([
  CarrierAndreani,
  CarrierCorreoArgentino,
  CarrierEPick
]);

const enviaInternationalOptions = new Set([
  NEEnviaDhlInternationalExpress,
  NEEnviaFedexInternationalGround
]);

const getCurrencyByCarrier = (
  carrierCode: string,
  deliveryOptionCode: string
): ECurrency => {
  if (carrierCode === CarrierEnvia) {
    return enviaInternationalOptions.has(deliveryOptionCode)
      ? ECurrency.UNITED_STATES
      : ECurrency.MEXICO;
  }

  if (argentinianCarriers.has(carrierCode)) {
    return ECurrency.ARGENTINA;
  }

  return ECurrency.BRAZIL;
};

export { format, getCurrencyByCarrier, getCurrencySymbol };
